/**
 * The Case Context Bar's Type field: the patient type on the pill, with a hover popover carrying the
 * type, an edit graphic, and Legal status / Place of service rows. `CaseContextBar` owns the
 * `LABEL | value` pill chrome; this renders the value half and the popover.
 *
 * `Popover` is used without `isLazy`, so its rows are always mounted and only visibility toggles
 * with hover/focus; tests can assert content without simulating hover.
 */
import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import { CMXIcon } from '@codametrix/ui-components';
import React from 'react';

import type { PatientTypeInfoData } from '../../interface/patientTypeInfo';
import type { SlotProps } from '../../interface/slotProps';
import {
  INK,
  INK_MUTED,
  INK_SOFT,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW
} from '../../styles/tokens';

const PLACEHOLDER = 'N/A';

const POPOVER_BG = '#ffffff';

/**
 * Targets the ROWS, not the whole popover: `aria-describedby` flattens its target's subtree into one
 * description, so pointing at the body would re-announce the title and read the edit graphic as
 * prose. Lives on an inner Box because Chakra owns and overwrites `PopoverContent`'s `id`, and is
 * per-instance because two of these on one page would otherwise share it.
 */
const useRowsId = (): string => `patient-type-info-rows-${React.useId()}`;

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

/**
 * `Number.isFinite` rather than `typeof v === 'number'`: `NaN`/`Infinity` are numbers, and a typeof
 * check would paint their literal text as though it were a real patient type.
 */
const present = (v: unknown): v is string | number =>
  Number.isFinite(v) || (typeof v === 'string' && v.trim() !== '');

/** Present strings are NOT trimmed. */
const na = (v: unknown): string => (present(v) ? String(v) : PLACEHOLDER);

/** `value` is `unknown`, not `string`: the cast below is unchecked, so `na()` is what actually
 *  makes a wrong-shaped field safe. */
interface PatientTypeInfoRow {
  readonly id: string;
  readonly label: string;
  readonly value?: unknown;
}

const PatientTypeInfoRowView: React.FC<{
  readonly row: PatientTypeInfoRow;
}> = ({ row }) => (
  <Flex
    justifyContent="space-between"
    gap="20px"
    lineHeight="1.6"
    data-testid={`patient-type-info-${row.id}`}
  >
    {/* The label never wraps — a long value should yield, not "Place of / service". */}
    <Text
      as="span"
      fontSize="11px"
      fontWeight={400}
      color={INK_SOFT}
      flexShrink={0}
    >
      {row.label}
    </Text>
    <Text
      as="span"
      fontSize="11.5px"
      fontWeight={600}
      color={INK}
      // `space-between` positions the box, not the text inside it, so a wrapped second line needs
      // this to stay flush with the first.
      textAlign="right"
      // Both rules: `overflowWrap` handles long words, `wordBreak` keeps an unbroken run from
      // widening the fixed-width card.
      overflowWrap="anywhere"
      wordBreak="break-word"
    >
      {na(row.value)}
    </Text>
  </Flex>
);

export const PatientTypeInfo: React.FC<SlotProps> = ({ value }) => {
  const rowsId = useRowsId();

  // Anything that is not a genuine record collapses to `{}`, so a wrong-shaped value falls back to
  // the placeholder instead of reaching JSX as a non-primitive child.
  const data: PatientTypeInfoData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as PatientTypeInfoData)
      : {};

  const patientType = na(data.patientType);

  const rows: readonly PatientTypeInfoRow[] = [
    { id: 'legal-status', label: 'Legal status', value: data.legalStatus },
    {
      id: 'place-of-service',
      label: 'Place of service',
      value: data.placeOfService
    }
  ];

  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Text
          as="span"
          tabIndex={0}
          // NOT `role="button"`: the hover popover attaches no `onClick`, and a <span> gets no
          // synthesized click from Enter/Space, so a button role would promise an activation that
          // does nothing.
          // The pill's "Type" caption is a sibling node owned by `CaseContextBar`, so without this
          // a focused trigger announces a bare value — or, on an absent value, just "N/A".
          aria-label={`Patient type: ${patientType}`}
          aria-describedby={rowsId}
          data-testid="patient-type-info"
          textStyle="Body 2"
          color={INK}
          // Truncation lives here, not on the host `Tag`: the patient type is the pill's only
          // unbounded value. `display` is explicit because `max-width`/`overflow`/`text-overflow`
          // do not apply to a non-replaced inline box, and `minWidth={0}` is what lets
          // `textOverflow` take effect inside the host's flex row.
          display="inline-block"
          maxWidth="14rem"
          minWidth={0}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          sx={HOVER_HINT_SX}
        >
          {patientType}
        </Text>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          data-testid="patient-type-info-popover"
          bg={POPOVER_BG}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="14px 16px"
          // Both props: `width` alone loses to Chakra's own `PopoverContent` max-width default.
          width="280px"
          maxWidth="280px"
        >
          <PopoverArrow bg={POPOVER_BG} />

          <Flex align="center" justifyContent="space-between" gap="12px">
            <Text
              as="span"
              textStyle="Body 1"
              color={INK}
              data-testid="patient-type-info-title"
              minWidth={0}
              // The title WRAPS where the trigger ellipsises. Its usable width (~208px after
              // padding, border, the icon and the gap) is narrower than the trigger's 14rem, so
              // truncating here too would leave a long type unreadable anywhere in the app.
              overflowWrap="anywhere"
              wordBreak="break-word"
            >
              {patientType}
            </Text>

            {/* A graphic, not a button: the hover popover does not trap focus and closes on trigger
                blur, so any focusable control here is unreachable by keyboard. Wiring the edit action
                means solving popover focus management first.
                `display="grid"` + `placeItems="center"` are load-bearing — without them the 12px icon
                sits top-left of its 26px box. */}
            <Box
              role="img"
              aria-label="Edit patient type"
              data-testid="patient-type-info-edit"
              cursor="default"
              flexShrink={0}
              display="grid"
              placeItems="center"
              w="26px"
              h="26px"
              color={INK_MUTED}
              border="1px solid var(--border-strong, #c9c8ce)"
              borderRadius="7px"
            >
              <CMXIcon svgIconName="BiomeEdit" svgIconSize="12" />
            </Box>
          </Flex>

          <Box
            borderTop={`1px solid ${POPOVER_BORDER}`}
            marginTop="12px"
            paddingTop="12px"
            data-testid="patient-type-info-divider"
          >
            <Flex
              id={rowsId}
              direction="column"
              gap="6px"
              data-testid="patient-type-info-rows"
            >
              {rows.map((row) => (
                <PatientTypeInfoRowView key={row.id} row={row} />
              ))}
            </Flex>
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
